// Copyright (c) 2021 WiseTime. All rights reserved.

import { timer, Observable, of, fromEvent, shareReplay, partition, takeUntil, repeat } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { never } from "grpc-web-rx"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { grpc } from "../../../app/rpc/executor"
import { notificationServiceClient } from "../../../app/rpc/client"
import {
  App,
  ListNotificationsRequest,
  ListNotificationsResponse,
} from "../../../generated/wisetime/user/notification/v4/notification_pb"

export type VisibilityChange = () => {
  pageVisible$: Observable<Event>,
  pageHidden$: Observable<Event>,
}
export const visibilityChange: VisibilityChange = () => {
  const [pageVisible$, pageHidden$] = partition(
    fromEvent(document, "visibilitychange").pipe(
      shareReplay({ refCount: true, bufferSize: 1 })
    ),
    () => document.visibilityState === "visible",
  )
  return { pageVisible$, pageHidden$ }
}

type ListNotificationsRpc = () => Observable<ListNotificationsResponse>

export const listNotifications = (
  listNotificationsRpc: ListNotificationsRpc,
  visibilityChange: VisibilityChange,
) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.listNotifications.match),
      mergeMap(action => {
        const { pageVisible$, pageHidden$ } = visibilityChange()
        return timer(5000, 120_000)
          .pipe(
            mergeMap(() => of(action).pipe(
              mergeMap(listNotificationsRpc),
              mergeMap(response => of(Slice.setNotifications(response.toObject().notificationsList))),
              catchError(_ => of(Actions.listNotificationsFailed())),
            )),
            takeUntil(pageHidden$),
            repeat({ delay: () => pageVisible$ }),
          )
      }),
    )

const listNotificationsRpc = (): Observable<ListNotificationsResponse> => (
  grpc(
    () => notificationServiceClient.listNotifications(
      new ListNotificationsRequest().setApp(App.WEB_TIMELINE),
      null,
    ),
    never,
  )
)

export default listNotifications(listNotificationsRpc, visibilityChange)
